import React, { useContext, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useMutation, useQueryClient } from "react-query"
import { useReactions } from "../../../hooks/useReactions"
import { mutationRequest } from "../../../util/request"
import { UserContext } from "../../HomeApp"
import { openAuthDialog } from "../../Authentication"
import clsx from "clsx"

interface CommentVoteBlockProps {
  contentId: number
  postId: number
}

interface UserReaction {
  id: number
  reaction_type: string
}

interface VoteCounts {
  up_vote?: number
  down_vote?: number
}

interface VoteData {
  counts: VoteCounts
  current_user_setting: UserReaction | null
}

const VOTES = [
  { type: "up_vote", emoji: "🤘", label: "Up vote" },
  { type: "down_vote", emoji: "👎", label: "Down vote" },
] as const

const CommentVoteBlock: React.FC<CommentVoteBlockProps> = ({ contentId, postId }) => {
  const dispatch = useDispatch()
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()
  const [userReaction, setUserReaction] = useState<UserReaction | null>(null)

  const { data, isLoading, isError, status } = useReactions(
    "Comment",
    contentId,
    "votes",
    true,
  ) as { data: VoteData; isLoading: boolean; isError: boolean; status: string }

  useEffect(() => {
    if (!isLoading && !isError && status === "success" && user) {
      setUserReaction(data.current_user_setting)
    }
  }, [isLoading, isError, status])

  const mutation = useMutation(mutationRequest)

  const submitVote = (voteType: string) => {
    if (!user) {
      dispatch(openAuthDialog({ explanation: "You must first sign up or log in to vote" }))
      return
    }
    mutation.mutate({
      options: { method: "POST" },
      path: `/api/v1/reactions`,
      data: {
        reaction: {
          id: userReaction?.id,
          content_type: "Comment",
          content_id: contentId,
          post_id: postId,
          reaction_type: userReaction?.reaction_type !== voteType ? voteType : "remove",
        },
        filter: "votes",
      },
    })
  }

  useEffect(() => {
    if (mutation.isSuccess) {
      setUserReaction((mutation.data as VoteData).current_user_setting)
      queryClient.invalidateQueries(["reactions", "Comment", contentId, "votes", true])
    }
  }, [mutation.isSuccess])

  if (isLoading) return null

  return (
    <div className="flex items-center gap-1">
      {VOTES.map(({ type, emoji, label }) => (
        <button
          key={type}
          type="button"
          onClick={() => submitVote(type)}
          aria-label={label}
          title={label}
          className="flex items-center gap-1 text-lg focus:outline-none"
          style={{ opacity: userReaction?.reaction_type === type ? 1 : 0.4 }}
        >
          <span role="img" aria-hidden="true">{emoji}</span>
          {(data?.counts?.[type] ?? 0) > 0 && (
            <span className="text-xs text-gray-500">{data.counts[type]}</span>
          )}
        </button>
      ))}
    </div>
  )
}

export default CommentVoteBlock
